import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonSpinner } from '@ionic/angular/standalone';
import { TranslateModule, TranslatePipe, _ } from '@ngx-translate/core';
import { filter, first, switchMap } from 'rxjs';
import { Category } from 'src/app/models/category';
import { AddressData } from 'src/app/models/create-offer/address-data';
import { CategoryData } from 'src/app/models/create-offer/category-data';
import { CreateOfferData } from 'src/app/models/create-offer/create-offer-data';
import { FilterData } from 'src/app/models/create-offer/filter-data';
import { MediaData } from 'src/app/models/create-offer/media-data';
import { PlatformData } from 'src/app/models/create-offer/platform-data';
import { PriceData } from 'src/app/models/create-offer/price-data';
import { Filter } from 'src/app/models/filter';
import { Offer } from 'src/app/models/offer';
import { OfferVariant } from 'src/app/models/offer-definition';
import { CreateOfferPage } from 'src/app/pages/create-offer/create-offer.page';
import { CategoryService } from 'src/app/services/category.service';
import { FilterService } from 'src/app/services/filter.service';
import { OfferService } from 'src/app/services/offer.service';
import { ToastService } from 'src/app/services/toast.service';
import { CategoryUtils } from 'src/app/utils/category.utils';
import { LoadingUtils } from 'src/app/utils/loading.utils';
import {
    applyWholesalePriceFieldsFromOffer,
    buildEditOfferVariantGroup,
    buildFilterValuesFromOfferDefinitions,
    buildManualVariantSelectionFromOffer,
    buildPackagesFromOffer,
    buildPriceVariantsMapFromOffer,
    buildVariantFilterValuesPatch,
    buildVariantPostsFromOffer,
} from 'src/app/utils/offer-payload.utils';
import { OfferUtils } from 'src/app/utils/offer.utils';

@Component({
    selector: 'app-profile-tab-update-offer',
    templateUrl: './profile-tab-update-offer.page.html',
    styleUrls: ['./profile-tab-update-offer.page.scss'],
    standalone: true,
    imports: [CommonModule, IonSpinner, TranslateModule, TranslatePipe, CreateOfferPage],
})
export class ProfileTabUpdateOfferPage implements OnInit {
    @ViewChild(CreateOfferPage) private createOfferPage?: CreateOfferPage;

    offer: Offer;
    data: CreateOfferData;
    loading = true;
    resubmitting = false;
    private category: Category;

    constructor(
        private readonly offerService: OfferService,
        private readonly toastService: ToastService,
        private readonly categoryService: CategoryService,
        private readonly filterService: FilterService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
    ) {}

    ngOnInit() {
        const offerId = this.route.snapshot.params['id'];
        if (!offerId) {
            this.router.navigate(['/profile/offers']);
            return;
        }

        this.categoryService.categories
            .pipe(filter((categories) => !!categories))
            .pipe(first())
            .subscribe({
                next: () => {
                    this.offerService.get(offerId).subscribe({
                        next: (offer) => {
                            this.offer = offer;
                            this.category = CategoryUtils.getById(this.offer.category);
                            if (!this.category) {
                                this.toastService.error(_('Error loading offer'));
                                this.router.navigate(['/profile/offers']);
                                return;
                            }
                            this.filterService.getFilters({ category: this.category.id, platform: this.offer.platform }).subscribe({
                                next: (filters) => {
                                    OfferUtils.hydrateFilters(this.offer, filters);
                                    OfferVariant.setEntities(this.offer.variants, filters);
                                    this.data = this.buildCreateOfferData(filters);
                                    this.loading = false;
                                },
                                error: () => {
                                    this.toastService.error(_('Error loading offer'));
                                    this.router.navigate(['/profile/offers']);
                                },
                            });
                        },
                        error: () => {
                            this.toastService.error(_('Error loading offer'));
                            this.router.navigate(['/profile/offers']);
                        },
                    });
                },
            });
    }

    get validationStatus(): string | undefined {
        return this.offer?.validationStatus;
    }

    get validationRejectionReason(): string | null | undefined {
        return this.offer?.validationRejectionReason;
    }

    onBoostSubmit() {
        if (this.validationStatus === 'rejected') {
            this.resubmitForValidation();
            return;
        }
        void this.createOfferPage?.saveOffer();
    }

    resubmitForValidation() {
        if (!this.offer || this.validationStatus !== 'rejected') {
            return;
        }
        if (!this.createOfferPage) {
            this.toastService.error(_('offer_resubmit_error'));
            return;
        }
        if (this.createOfferPage.isSavingOffer || this.resubmitting) {
            return;
        }
        this.resubmitting = true;
        void LoadingUtils.show();

        this.createOfferPage
            .saveOfferUpdate$()
            .pipe(switchMap(() => this.offerService.resubmitOffer(this.offer.id)))
            .subscribe({
                next: (o) => {
                    this.offer = o;
                    this.resubmitting = false;
                    LoadingUtils.hide();
                    void this.toastService.success(_('offer_resubmit_success'));
                    void this.router.navigate(['/profile/offers']);
                },
                error: (err) => {
                    this.resubmitting = false;
                    LoadingUtils.hide();
                    const message = this.createOfferPage!.formatBackendErrorForParent(err, _('offer_resubmit_error'));
                    this.toastService.error(message);
                },
            });
    }

    private buildCreateOfferData(filters: Filter[]): CreateOfferData {
        const d = CreateOfferData.createEmpty();
        d.platformData = new PlatformData();
        d.platformData.platform = this.offer.platform;
        d.categoryData = new CategoryData();
        d.categoryData.category = this.category;
        d.filterData = new FilterData();
        d.filterData.name = this.offer.name;
        d.filterData.description = this.offer.description;
        d.filterData.filters = filters;
        d.filterData.filterValues = buildFilterValuesFromOfferDefinitions(this.offer, filters);
        d.filterData.filterVariantValues = buildVariantFilterValuesPatch(this.offer, filters) as typeof d.filterData.filterVariantValues;
        d.filterData.manualVariantSelection = buildManualVariantSelectionFromOffer(this.offer, filters);
        d.filterData.variants = buildVariantPostsFromOffer(this.offer, filters);
        const restoredGroup = buildEditOfferVariantGroup(this.offer, filters, d.filterData.manualVariantSelection, d.filterData.variants);
        d.filterData.variantGroups = restoredGroup ? [restoredGroup] : [];
        if (this.offer.sizeGuide?.id) {
            d.filterData.sizeGuide = this.offer.sizeGuide.id;
        }
        d.filterData.documents = (this.offer.documents || [])
            .filter((doc) => doc.media?.id)
            .map((doc) => ({
                type: doc.type,
                mediaId: doc.media.id,
                expiresAt: doc.expiresAt ?? null,
            }));
        d.mediaData = new MediaData();
        const medias = [...(this.offer.medias || [])];
        d.mediaData.medias = medias;
        if (restoredGroup) {
            restoredGroup.medias = [...medias];
        }
        d.priceData = new PriceData();
        d.priceData.referencePrice = this.offer.referencePrice;
        d.priceData.referenceCurrency = this.offer.referenceCurrency as PriceData['referenceCurrency'];
        applyWholesalePriceFieldsFromOffer(d.priceData, this.offer);
        d.priceData.vat = this.offer.vat?.id;
        d.priceData.daysToShip = this.offer.daysToShip;
        d.priceData.shippingSubsidyPercent = this.offer.shippingSubsidyPercent ?? 0;
        d.priceData.stocks = this.offer.stocks;
        d.priceData.sku = this.offer.sku;
        d.priceData.packages = buildPackagesFromOffer(this.offer);
        d.priceData.variants = buildPriceVariantsMapFromOffer(this.offer.variants, d.filterData.variants);
        d.addressData = new AddressData();
        d.addressData.address = this.offer.address;
        return d;
    }
}
